import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { ChevronLeft, History, Settings } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { useAdminConsentGate } from '@/features/internal-admin/lib/use-admin-consent-gate';
import { McpConfigContent } from '@/app/routes/mcp-servers/id/mcp-config';
import {
  McpHistoryContent,
  FetchRunsParams,
} from '@/app/routes/mcp-servers/id/runs/mcp-runs';
import {
  McpWithTools,
  isNil,
  assertNotNullOrUndefined,
} from '@activepieces/shared';

const tabs = [
  { name: 'Configure', value: 'configure', icon: Settings },
  { name: 'History', value: 'history', icon: History },
];

const AdminMcpDetailPage = () => {
  const { tenantId, mcpId } = useParams();
  const navigate = useNavigate();

  assertNotNullOrUndefined(tenantId, 'tenantId is not set');
  assertNotNullOrUndefined(mcpId, 'mcpId is not set');

  const { canLoadCustomerData, consentDialog } = useAdminConsentGate(tenantId);

  const {
    data: mcp,
    isLoading,
    isError,
  } = useQuery<McpWithTools, Error>({
    queryKey: ['admin-mcp', tenantId, mcpId],
    queryFn: () => adminApi.getMcp(tenantId, mcpId),
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const fetchRuns = useCallback(
    (params: FetchRunsParams) => adminApi.listMcpRuns(tenantId, mcpId, params),
    [tenantId, mcpId],
  );

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('MCP not found')}</p>
      </div>
    );
  }

  if (isLoading || isNil(mcp)) {
    return (
      <div className="bg-background flex h-screen w-screen items-center justify-center">
        <LoadingSpinner size={50} />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="p-2"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">{mcp.name}</h1>
      </div>
      <Tabs defaultValue={tabs[0].value} className="w-full">
        <TabsList variant="outline">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} variant="outline">
              <tab.icon className="h-4 w-4 mr-2" /> {t(tab.name)}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="w-full mt-4">
          {tabs.map((tab) => (
            <TabsContent
              key={tab.value}
              value={tab.value}
              className="w-full mt-0"
            >
              {tab.value === 'configure' && (
                <McpConfigContent mcp={mcp} readOnly />
              )}
              {tab.value === 'history' &&
                (consentDialog ??
                  (canLoadCustomerData && (
                    <McpHistoryContent fetchRuns={fetchRuns} mcpId={mcpId} />
                  )))}
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
};

export default AdminMcpDetailPage;
